import { useState, useEffect, useCallback, useRef } from 'react';
import { getSessionUser } from "../../utils/auth/sessionUser";
import { isAppSession, useAuthSession } from '../../lib/http/session';
import { isPatientSession } from '../../utils/userPrefs/validatePermissions';
import CommunicationsService from '../../services/CommunicationsService';
import { ConnectionState as MessagingConnectionState } from '../../services/CommunicationsService';
import { useMessagingPanel } from '../../context/MessagingContext';
import {
	Conversation,
	Message,
	MessageSentEvent,
	MessageReadEvent,
	TypingIndicatorEvent,
	ConversationAssignedEvent,
	ParticipantAddedEvent,
	ParticipantRemovedEvent,
	ConversationQueuedEvent,
} from '../../types/messaging';
import {
	getConversations,
	getQueueConversations,
	getMessages,
	sendMessage,
	markConversationAsRead,
} from '../../api/messaging';
import type { UserUuid } from '../../types/identity';

interface UseMessagingOptions {
	autoConnect?: boolean;
	autoLoadConversations?: boolean;
}

export const useMessaging = (options: UseMessagingOptions = {}) => {
	const { autoConnect = true, autoLoadConversations = true } = options;

	// Access context so we can keep the background badge in sync when marking as read
	const { markConversationAsRead: contextMarkAsRead } = useMessagingPanel();

	const [conversations, setConversations] = useState<Conversation[]>([]);
	const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
	const [messages, setMessages] = useState<Record<string, Message[]>>({});
	const [typingUsers, setTypingUsers] = useState<Record<string, UserUuid[]>>({});
	const [connectionState, setConnectionState] = useState<MessagingConnectionState>('disconnected');
	const [isLoading, setIsLoading] = useState(false);
	const [userId, setUserId] = useState<UserUuid | null>(null);
	const [queueConversations, setQueueConversations] = useState<Conversation[]>([]);

	const getUserData = async () => {
		const userData = getSessionUser();
		if (!userData.id) {
			console.warn('messaging.session_user.missing');
			return {};
		}
		setUserId(userData.id);
	};

	useEffect(() => {
		getUserData();
	}, []);

	const isLoggedIn = isAppSession(useAuthSession());


	const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

	// Load unassigned queue conversations (practitioners only — no participant restriction)
	const loadQueueConversations = useCallback(async () => {
		if (!isLoggedIn || isPatientSession()) return;
		try {
			const data = await getQueueConversations();
			setQueueConversations(data);
		} catch (error) {
			console.error('Error loading queue conversations:', error);
		}
	}, [isLoggedIn]);

	// Load conversations
	const loadConversations = useCallback(async () => {
		if (!userId || !isLoggedIn) return;

		setIsLoading(true);
		try {
			const data = await getConversations({
				user_id: userId,
			});
			setConversations(data);
		} catch (error) {
			console.error('Error loading conversations:', error);
		} finally {
			setIsLoading(false);
		}
	}, [userId, isLoggedIn]);

	// Load messages for a conversation
	const loadMessages = useCallback(
		async (conversationId: string, limit: number = 50) => {
			if (!userId) return;

			try {
				const data = await getMessages(conversationId, {
					user_id: userId,
					limit,
				});
				setMessages((prev) => ({
					...prev,
					[conversationId]: data,
				}));
			} catch (error) {
				console.error('Error loading messages:', error);
			}
		},
		[userId]
	);

	// Send a message
	const sendNewMessage = useCallback(
		async (conversationId: string, content: string, files?: File[]) => {
			if (!userId) return null;

			// Determine message type based on attachments
			const hasFiles = files && files.length > 0;
			let messageType: 'text' | 'file' | 'image' = 'text';
			if (hasFiles) {
				const hasImages = files.some(f => f.type.startsWith('image/'));
				messageType = hasImages ? 'image' : 'file';
			}

			// Optimistic update — message appears instantly for the sender
			const tempId = `temp-${Date.now()}`;
			const optimisticMessage: Message = {
				id: tempId,
				conversation_id: conversationId,
				sender_user_id: userId,
				type: messageType,
				content: content || null,
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
				attachments: hasFiles
					? files.map((file, index) => ({
						id: `temp-attachment-${index}`,
						name: file.name,
						size: file.size,
						type: file.type,
						url: '', // Will be populated by server response
					}))
					: null,
				reply_to_message_id: null,
				is_edited: false,
				edited_at: null,
				is_deleted: false,
				deleted_at: null,
				metadata: hasFiles ? { uploading: true } : null,
			};

			setMessages((prev) => ({
				...prev,
				[conversationId]: [...(prev[conversationId] || []), optimisticMessage],
			}));

			try {
				const message = await sendMessage(conversationId, {
					sender_user_id: userId,
					content: content || ' ',
					type: messageType,
					files,
				});

				// Replace the temporary optimistic message with the confirmed server message
				setMessages((prev) => ({
					...prev,
					[conversationId]: (prev[conversationId] || []).map((m) =>
						m.id === tempId ? message : m
					),
				}));

				return message;
			} catch (error) {
				// Remove the optimistic message on failure
				setMessages((prev) => ({
					...prev,
					[conversationId]: (prev[conversationId] || []).filter((m) => m.id !== tempId),
				}));
				console.error('Error sending message:', error);
				throw error;
			}
		},
		[userId]
	);

	// Mark conversation as read
	const markAsRead = useCallback(
		async (conversationId: string) => {
			if (!userId) return;

			try {
				await markConversationAsRead(conversationId, { user_id: userId });

				// Update local UI state
				setConversations((prev) =>
					prev.map((conv) =>
						conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
					)
				);

				// Also clear the badge count maintained by the background context
				contextMarkAsRead(conversationId);
			} catch (error) {
				console.error('Error marking conversation as read:', error);
			}
		},
		[userId, contextMarkAsRead]
	);

	// Subscribe to a conversation
	const subscribeToConversation = useCallback((conversationId: string) => {
		CommunicationsService.subscribeToConversation(conversationId);
	}, []);

	// Unsubscribe from a conversation
	const unsubscribeFromConversation = useCallback((conversationId: string) => {
		CommunicationsService.unsubscribeFromConversation(conversationId);
	}, []);

	// WebSocket event handlers
	const handleMessageSent = useCallback(
		(event: MessageSentEvent) => {
			const conversationId = event.conversation_id;

			// Add message to local state (dedup against optimistic update)
			setMessages((prev) => {
				const existing = prev[conversationId] || [];
				if (existing.some((m) => m.id === event.id)) return prev;
				return { ...prev, [conversationId]: [...existing, event as unknown as Message] };
			});

			// Update conversation last-message timestamp.
			// NOTE: badge (unread_count) is intentionally NOT incremented here.
			// The MessagingContext handles badge increments exclusively via
			// onNotificationReceived, preventing double-increments.
			setConversations((prev) =>
				prev.map((conv) =>
					conv.id === conversationId ? { ...conv, last_message_at: event.created_at } : conv
				)
			);

			// If the user is actively viewing this conversation, mark it as read
			// immediately so the badge (set by MessagingContext) resets to 0.
			setActiveConversationId((currentActive) => {
				if (currentActive === conversationId && event.sender_user_id !== userId) {
					markAsRead(conversationId);
				}
				return currentActive;
			});

			// Clear typing indicator for sender
			const senderTimerKey = `${conversationId}:${event.sender_user_id}`;
			if (typingTimers.current[senderTimerKey]) {
				clearTimeout(typingTimers.current[senderTimerKey]);
				delete typingTimers.current[senderTimerKey];
			}
			setTypingUsers((prev) => ({
				...prev,
				[conversationId]: (prev[conversationId] || []).filter(
					(id) => id !== event.sender_user_id
				),
			}));
		},
		[userId, markAsRead]
	);

	const handleMessageRead = useCallback((event: MessageReadEvent) => {
		const conversationId = event.conversation_id;
		const messageId = event.message_id;

		// Update read receipts in local state
		setMessages((prev) => ({
			...prev,
			[conversationId]: (prev[conversationId] || []).map((msg) =>
				msg.id === messageId
					? {
						...msg,
						read_by: [...(msg.read_by || []), event.user_id],
					}
					: msg
			),
		}));
	}, []);

	const handleTypingIndicator = useCallback((event: TypingIndicatorEvent) => {
		const conversationId = event.conversation_id;
		const timerKey = `${conversationId}:${event.user_id}`;

		if (event.is_typing) {
			setTypingUsers((prev) => ({
				...prev,
				[conversationId]: [...new Set([...(prev[conversationId] || []), event.user_id])],
			}));

			// Clear previous timer for this specific user
			if (typingTimers.current[timerKey]) {
				clearTimeout(typingTimers.current[timerKey]);
			}

			// Set timer to remove typing indicator after 5 seconds
			typingTimers.current[timerKey] = setTimeout(() => {
				setTypingUsers((prev) => ({
					...prev,
					[conversationId]: (prev[conversationId] || []).filter((id) => id !== event.user_id),
				}));
				delete typingTimers.current[timerKey];
			}, 5000);
		} else {
			setTypingUsers((prev) => ({
				...prev,
				[conversationId]: (prev[conversationId] || []).filter((id) => id !== event.user_id),
			}));

			if (typingTimers.current[timerKey]) {
				clearTimeout(typingTimers.current[timerKey]);
				delete typingTimers.current[timerKey];
			}
		}
	}, []);

	const handleConversationAssigned = useCallback(
		(event: ConversationAssignedEvent) => {
			// Reload conversations (user's own list) and the queue count
			loadConversations();
			loadQueueConversations();
		},
		[loadConversations, loadQueueConversations]
	);

	const handleParticipantAdded = useCallback(
		(event: ParticipantAddedEvent) => {
			// Someone was added to a conversation we're already in — reload to sync participants
			loadConversations();
		},
		[loadConversations]
	);

	const handleAddedToConversation = useCallback(
		(event: ParticipantAddedEvent) => {
			// The current user was added to a new conversation — reload the list
			// and subscribe to the new conversation channel so we receive its messages.
			loadConversations();
			CommunicationsService.subscribeToConversation(event.conversation_id);
		},
		[loadConversations]
	);

	const handleParticipantRemoved = useCallback(
		(event: ParticipantRemovedEvent) => {
			// Someone was removed from a conversation we're in — reload to sync participants
			loadConversations();
		},
		[loadConversations]
	);

	const handleRemovedFromConversation = useCallback(
		(event: ParticipantRemovedEvent) => {
			// The current user was removed — unsubscribe from the channel and clear the conversation
			CommunicationsService.unsubscribeFromConversation(event.conversation_id);
			setConversations((prev) => prev.filter((c) => c.id !== event.conversation_id));
			setActiveConversationId((prev) => (prev === event.conversation_id ? null : prev));
		},
		[] // intentionally no deps — reads no React state, only calls stable service method
	);

	const handleQueueConversationAdded = useCallback(
		(event: ConversationQueuedEvent) => {
			// Refresh both the queue count (for practitioners) and the user's own list
			loadQueueConversations();
			loadConversations();
		},
		[loadQueueConversations, loadConversations]
	);

	const handleConnectionStateChanged = useCallback(
		(state: MessagingConnectionState) => {
			setConnectionState(state);
			if (state === 'connected' && autoLoadConversations) {
				loadConversations();
			}
		},
		[autoLoadConversations, loadConversations]
	);

	// Setup WebSocket listeners
	useEffect(() => {
		if (!isLoggedIn || !userId || !autoConnect) {
			// Do NOT disconnect here — CommunicationsService is a shared singleton
			// owned by MessagingContext. Only clear local panel state.
			setConversations([]);
			setMessages({});
			setConnectionState('disconnected');
			return;
		}

		// Register all panel-level handlers under an isolated namespace.
		// This never overwrites handlers registered by MessagingContext.
		CommunicationsService.registerListeners('messaging-panel', {
			onMessageSent: handleMessageSent,
			onMessageRead: handleMessageRead,
			onTypingIndicator: handleTypingIndicator,
			onConversationAssigned: handleConversationAssigned,
			onParticipantAdded: handleParticipantAdded,
			onAddedToConversation: handleAddedToConversation,
			onParticipantRemoved: handleParticipantRemoved,
			onRemovedFromConversation: handleRemovedFromConversation,
			onQueueConversationAdded: handleQueueConversationAdded,
			onConnectionStateChanged: handleConnectionStateChanged,
		});

		// Sync current state immediately — the service may already be connected
		// and won't fire onConnectionStateChanged again.
		setConnectionState(CommunicationsService.getConnectionState());

		// connect() is idempotent — no-op if already connected for this userId
		CommunicationsService.connect(userId);

		if (autoLoadConversations) {
			loadConversations();
			loadQueueConversations();
		}

		return () => {
			CommunicationsService.unregisterListeners('messaging-panel');
			Object.values(typingTimers.current).forEach((timer) => clearTimeout(timer));
			typingTimers.current = {};
			setTypingUsers({});
		};
	}, [
		isLoggedIn,
		userId,
		autoConnect,
		autoLoadConversations,
		handleMessageSent,
		handleMessageRead,
		handleTypingIndicator,
		handleConversationAssigned,
		handleParticipantAdded,
		handleAddedToConversation,
		handleParticipantRemoved,
		handleRemovedFromConversation,
		handleQueueConversationAdded,
		handleConnectionStateChanged,
		loadConversations,
		loadQueueConversations,
	]);

	// Handle visibility change
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && userId && isLoggedIn) {
				if (CommunicationsService.getConnectionState() === 'disconnected') {
					CommunicationsService.connect(userId);
				}
				if (autoLoadConversations) {
					loadConversations();
					loadQueueConversations();
				}
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [userId, isLoggedIn, autoLoadConversations, loadConversations, loadQueueConversations]);

	// Get total unread count
	const totalUnreadCount = conversations.reduce(
		(total, conv) => total + (conv.unread_count || 0),
		0
	);

	// Unassigned queue count — only meaningful for practitioners.
	// Patients see 0 (it's their own conversation waiting, not an alert they need).
	// Practitioners use the separate queueConversations list which has NO participant filter.
	const queueCount = isPatientSession()
		? 0
		: queueConversations.filter(
			(conv) => conv.assignment?.status !== 'assigned'
		  ).length;

	// Get unread conversations count
	const unreadConversationsCount = conversations.filter((conv) => (conv.unread_count || 0) > 0)
		.length;

	return {
		// State
		conversations,
		activeConversationId,
		messages,
		typingUsers,
		connectionState,
		isLoading,
		totalUnreadCount,
		queueCount,
		queueConversations,
		unreadConversationsCount,
		userId,

		// Actions
		setActiveConversationId,
		loadConversations,
		loadMessages,
		sendNewMessage,
		markAsRead,
		subscribeToConversation,
		unsubscribeFromConversation,
	};
};